import { api, ApiError } from '../lib/api.js';
import { applyBranding, esc } from '../lib/brand.js';
import { shell, centered, loading, type NavItem } from '../lib/shell.js';
import { navigate } from '../lib/router.js';
import { toast } from '../lib/toast.js';
import { icon } from '../lib/icons.js';
import { isPlatformAdmin } from '../lib/auth.js';

interface Me { authenticated: boolean; user: { email: string; realmRoles: string[] } }
interface Agency {
  id: string; name: string; slug: string; status: string; created_at: string;
  branding: Record<string, string>;
  business_count: number; form_count: number; submission_count: number; staff_count: number;
}
interface Staff { user_id: string; email: string; name: string | null; role: string; last_login_at: string | null }

const NAV: NavItem[] = [{ key: 'agencies', href: '/platform', label: 'Agencies', ico: icon('building2') }];

export async function renderPlatform(root: HTMLElement): Promise<void> {
  root.innerHTML = loading();
  // Apex-owned console: reset any agency theme left on :root. The monochrome
  // palette itself comes from .apex-chrome on the shell, not from here.
  applyBranding(null);

  let me: Me;
  try { me = await api.get<Me>('/auth/me'); } catch { me = { authenticated: false } as Me; }
  if (!me.authenticated) { navigate('/login'); return; }
  if (!isPlatformAdmin(me.user.realmRoles)) { root.innerHTML = centered('Platform admin access required.'); return; }

  let agencies: Agency[] = [];
  let body: string;
  try {
    agencies = (await api.get<{ agencies: Agency[] }>('/api/platform/agencies')).agencies;
    const rows = agencies.length
      ? agencies.map((a) => `<tr class="clickable" data-manage="${a.id}">
          <td><strong>${esc(a.name)}</strong><br/><span class="muted" style="font-size:12.5px">${esc(a.slug)}</span></td>
          <td>${a.business_count}</td><td>${a.form_count}</td><td>${a.submission_count}</td><td>${a.staff_count}</td>
          <td><span class="badge ${a.status === 'active' ? 'green' : 'gray'}">${esc(a.status)}</span></td>
          <td style="white-space:nowrap">
            <button class="btn ghost sm" data-manage="${a.id}">Manage</button>
            <button class="btn sm enterbtn" data-id="${a.id}" data-name="${esc(a.name)}">Enter view →</button>
          </td></tr>`).join('')
      : `<tr><td colspan="7" class="muted" style="text-align:center;padding:34px">No agencies yet. Onboard your first reseller.</td></tr>`;
    body = `<div class="tiles" style="grid-template-columns:repeat(3,1fr);max-width:560px">
        <div class="tile"><div class="n accent">${agencies.length}</div><div class="l">Agencies</div></div>
        <div class="tile"><div class="n">${agencies.filter((a) => a.status === 'active').length}</div><div class="l">Active</div></div>
        <div class="tile"><div class="n">${agencies.reduce((s, a) => s + a.submission_count, 0)}</div><div class="l">Total submissions</div></div>
      </div>
      <div class="card"><div class="card-head"><h2>Reseller agencies</h2></div><div class="card-body flush">
        <table><thead><tr><th>Agency</th><th>Businesses</th><th>Forms</th><th>Subs</th><th>Staff</th><th>Status</th><th></th></tr></thead>
        <tbody>${rows}</tbody></table>
      </div></div>`;
  } catch (err) {
    body = `<div class="note">Failed to load agencies: ${esc(err instanceof ApiError ? err.message : 'error')}</div>`;
  }

  root.innerHTML = shell({
    brandName: 'Apex Harbor', sub: 'Harbor Platform', nav: NAV, activeKey: 'agencies', apex: true,
    title: 'Agencies', headAction: `<button class="btn" id="newAgency">${icon('plus')} New agency</button>`,
    userEmail: me.user.email, userRole: 'Platform admin', body,
  });

  (root.querySelector('#newAgency') as HTMLButtonElement).onclick = () => openNewAgency(root);
  root.querySelectorAll<HTMLElement>('[data-manage]').forEach((el) => {
    el.onclick = (e) => { e.stopPropagation(); openManage(root, el.dataset.manage!); };
  });
  root.querySelectorAll<HTMLButtonElement>('.enterbtn').forEach((btn) => {
    btn.onclick = (e) => { e.stopPropagation(); enterAgency(btn.dataset.id!, btn.dataset.name!); };
  });
}

async function enterAgency(id: string, name: string) {
  try {
    await api.post(`/api/platform/agencies/${id}/impersonate`);
    toast(`Entering ${name}…`);
    navigate('/app/overview');
  } catch (e) { toast(e instanceof ApiError ? e.message : 'Failed', 'err'); }
}

/* ---------------- Manage agency drawer ---------------- */
async function openManage(root: HTMLElement, id: string) {
  let data: { agency: Agency; staff: Staff[] };
  try { data = await api.get(`/api/platform/agencies/${id}`); }
  catch (e) { toast(e instanceof ApiError ? e.message : 'Failed to load agency', 'err'); return; }
  const { agency, staff } = data;
  const b = agency.branding || {};

  const staffRows = staff.length
    ? staff.map((s) => `<div class="row spread" style="font-size:13px;padding:6px 0;border-bottom:1px solid var(--border)">
        <span>${esc(s.email)}${s.last_login_at ? '' : ' <span class="badge gray">invite pending</span>'}</span>
        <span class="row" style="gap:6px">${s.last_login_at ? '' : `<button class="btn ghost sm m_reinvite" data-id="${s.user_id}" data-email="${esc(s.email)}">Resend</button>`}<span class="badge gray">${esc(s.role)}</span></span></div>`).join('')
    : '<div class="muted" style="font-size:13px">No staff yet.</div>';

  const scrim = openScrim(root, `
    <div class="dh"><h3>${esc(agency.name)} <span class="badge ${agency.status === 'active' ? 'green' : 'gray'}" style="margin-left:6px">${esc(agency.status)}</span></h3><button class="close-x">×</button></div>
    <div class="db">
      <div class="tiles" style="grid-template-columns:repeat(4,1fr);gap:8px;margin-bottom:18px">
        <div class="tile" style="padding:10px"><div class="n" style="font-size:20px">${agency.business_count}</div><div class="l">Businesses</div></div>
        <div class="tile" style="padding:10px"><div class="n" style="font-size:20px">${agency.form_count}</div><div class="l">Forms</div></div>
        <div class="tile" style="padding:10px"><div class="n" style="font-size:20px">${agency.submission_count}</div><div class="l">Subs</div></div>
        <div class="tile" style="padding:10px"><div class="n" style="font-size:20px">${agency.staff_count}</div><div class="l">Staff</div></div>
      </div>
      <button class="btn block" id="m_enter">Enter agency view →</button>

      <div class="section-title">Status</div>
      <button class="btn secondary sm" id="m_status">${agency.status === 'active' ? 'Suspend agency' : 'Activate agency'}</button>

      <div class="section-title">Branding</div>
      <div class="grid-2">
        <div class="field"><label>Primary</label><input type="color" id="m_primary" value="${esc(b.primary_color || '#1b2a5b')}" style="height:40px;padding:4px" /></div>
        <div class="field"><label>Accent</label><input type="color" id="m_accent" value="${esc(b.accent_color || '#2e7d32')}" style="height:40px;padding:4px" /></div>
      </div>
      <div class="field"><label>Support email</label><input type="email" id="m_email" value="${esc(b.support_email || '')}" /></div>
      <div class="field"><label>Footer text</label><input type="text" id="m_footer" value="${esc(b.footer_text || '')}" /></div>
      <button class="btn sm" id="m_saveBrand">Save branding</button>

      <div class="section-title">Staff</div>
      <div id="m_staff">${staffRows}</div>
      <div class="grid-2" style="margin-top:10px"><input type="email" id="m_staffEmail" placeholder="staff@agency.com" /><select id="m_staffRole"><option value="agency_admin">Admin</option><option value="agency_staff">Staff</option></select></div>
      <button class="btn sm" id="m_addStaff" style="margin-top:8px">Add staff</button>
    </div>`);

  const $ = (id: string) => scrim.querySelector('#' + id) as HTMLInputElement;
  (scrim.querySelector('#m_enter') as HTMLButtonElement).onclick = () => enterAgency(agency.id, agency.name);
  (scrim.querySelector('#m_status') as HTMLButtonElement).onclick = async () => {
    const status = agency.status === 'active' ? 'suspended' : 'active';
    try { await api.patch(`/api/platform/agencies/${id}`, { status }); toast(`Agency ${status}`); scrim.remove(); openManage(root, id); renderPlatform(root); }
    catch (e) { toast(e instanceof ApiError ? e.message : 'Failed', 'err'); }
  };
  (scrim.querySelector('#m_saveBrand') as HTMLButtonElement).onclick = async () => {
    try { await api.patch(`/api/platform/agencies/${id}`, { branding: { primary_color: $('m_primary').value, accent_color: $('m_accent').value, support_email: $('m_email').value, footer_text: $('m_footer').value } }); toast('Branding saved'); }
    catch (e) { toast(e instanceof ApiError ? e.message : 'Failed', 'err'); }
  };
  (scrim.querySelector('#m_addStaff') as HTMLButtonElement).onclick = async () => {
    const email = $('m_staffEmail').value.trim(); if (!email) return;
    const role = (scrim.querySelector('#m_staffRole') as HTMLSelectElement).value;
    try {
      const res = await api.post<{ warning?: string | null; invited?: boolean }>(`/api/platform/agencies/${id}/staff`, { email, role });
      toast(res?.warning ? `Staff added — ${res.warning}` : res?.invited ? `Invite sent to ${email}` : 'Staff added'); scrim.remove(); openManage(root, id); renderPlatform(root);
    }
    catch (e) { toast(e instanceof ApiError ? e.message : 'Failed', 'err'); }
  };
  scrim.querySelectorAll<HTMLButtonElement>('.m_reinvite').forEach((btn) => {
    btn.onclick = async () => {
      btn.disabled = true;
      try { await api.post(`/api/platform/agencies/${id}/staff/${btn.dataset.id}/invite`); toast(`Invite re-sent to ${btn.dataset.email}`); }
      catch (e) { toast(e instanceof ApiError ? e.message : 'Failed', 'err'); btn.disabled = false; }
    };
  });
}

/* ---------------- New agency drawer ---------------- */
function openNewAgency(root: HTMLElement) {
  const scrim = openScrim(root, `
    <div class="dh"><h3>Onboard a new agency</h3><button class="close-x">×</button></div>
    <div class="db">
      <div class="field"><label>Agency name *</label><input type="text" id="a_name" placeholder="e.g. Coastal Assurance Partners" /></div>
      <div class="field"><label>Slug *</label><input type="text" id="a_slug" placeholder="coastal-assurance" /><span class="hint">Stable identifier for the agency.</span></div>
      <div class="grid-2">
        <div class="field"><label>Primary color</label><input type="color" id="a_primary" value="#1b2a5b" style="height:42px;padding:4px" /></div>
        <div class="field"><label>Accent color</label><input type="color" id="a_accent" value="#2e7d32" style="height:42px;padding:4px" /></div>
      </div>
      <button class="btn block" id="a_submit">Create agency</button>
      <div id="a_err" class="note" style="display:none;margin-top:12px;background:#fdecea;border-color:#f5c6c2;color:#a3261c"></div>
    </div>`);
  const v = (id: string) => (scrim.querySelector('#' + id) as HTMLInputElement).value.trim();
  const errBox = scrim.querySelector('#a_err') as HTMLElement;
  (scrim.querySelector('#a_submit') as HTMLButtonElement).onclick = async () => {
    const name = v('a_name'); const slug = v('a_slug');
    if (!name || !slug) { errBox.textContent = 'Name and slug are required'; errBox.style.display = 'block'; return; }
    try {
      await api.post('/api/platform/agencies', { name, slug, branding: { primary_color: v('a_primary'), accent_color: v('a_accent') } });
      toast('Agency created');
      navigate('/platform');
    } catch (e) { errBox.textContent = e instanceof ApiError ? e.message : 'Create failed'; errBox.style.display = 'block'; }
  };
}

function openScrim(root: HTMLElement, drawerInner: string): HTMLElement {
  const scrim = document.createElement('div');
  scrim.className = 'scrim';
  scrim.innerHTML = `<div class="drawer">${drawerInner}</div>`;
  const close = () => scrim.remove();
  scrim.onclick = (e) => { if (e.target === scrim) close(); };
  scrim.querySelector('.close-x')?.addEventListener('click', close);
  root.appendChild(scrim);
  return scrim;
}
